import { createRoot, Root } from 'react-dom/client';
import { Map as OLMap, MapBrowserEvent, Overlay } from 'ol';
import Vector from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { Point, LineString, Polygon } from 'ol/geom';
import { transform } from 'ol/proj';
import {
  Style, Icon, Stroke,
} from 'ol/style';
import {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { Coordinate } from 'ol/coordinate';
import { availableProjections, destinationProjection } from 'configs/projections';
import PlaneInfoModal from 'components/PlaneInfoModal/index';
import { useFindPositionsByDateTimeRangeMutation, useNotifyADSBInDangerMutation } from 'services/adsb';
import { findNearestPlaneObservationByTimestamp, utcDayjs } from 'utils';
import { PlaneObservation } from 'services/adsb/dto';
import ARENALayerConfig from 'configs/connectedLayersConfig/ARENA';
import { useSelector } from 'react-redux';
import { parsePlaneData } from 'contexts/ADSBContext/messageParser';
import TimeSeriesContext from 'contexts/TimeSeriesContext';
import MainAppContext from 'contexts/MainAppContext';
import ADSBContext from '../../ADSBContext';
import { ADSBPositions } from '../../ADSBContext/models';
import { buildPlaneTrail } from '../../ADSBContext/planeHistory';
import LayerName from './enums';
import PlaneIcon from '../../../assets/icons/plane.svg';
import zIndexes from '../../../configs/zIndexes';
import { extractARENAAlarmasFromRootState, transformARENAAlertsIntoLogString } from './utils';
import disposeLayerAfterRender from '../../../utils/layerLifecycle';

const ADSB_ALERT_TIMEOUT_THRESHOLD = Number(process.env.REACT_APP_ADSB_ALERT_TIMEOUT_THRESHOLD)
  || 60000;
const MAIN_AIRPORT = process.env.REACT_APP_MAIN_AIRPORT_NAME || '';
const DANGER_COLOR = '#F44336';
const LABEL_ROOT_KEY = 'reactRoot';

function useADSBLayer(map: OLMap) {
  const {
    currentPositions, isConnected, activePlanePositions, pastAdsbMissing,
    adsbConnectionError, setPastAdsbMissing, updateActivePlane,
  } = useContext(ADSBContext);
  const { currentPosition, timestampList } = useContext(TimeSeriesContext);
  const { realTimeMode, adsbIsEnabled, currentProjection } = useContext(MainAppContext);

  const [findPositionsByDateTimeRange,
    { data: preloadedData, isSuccess: preloadDataSuccess, error: preloadError },
  ] = useFindPositionsByDateTimeRangeMutation();
  const [notifyADSBInDanger] = useNotifyADSBInDangerMutation();
  const [
    preloadCurrentPositions, setPreloadedCurrentPositions,
  ] = useState<PlaneObservation | null>(null);
  const [planesInDanger] = useState<Map<string, string>>(() => new Map());
  // Stop functions of the "plane in danger" blinking timers that are still running.
  const blinkStoppers = useRef<Set<() => void>>(new Set());

  const stopAllBlinking = useCallback(() => {
    Array.from(blinkStoppers.current).forEach((stop) => stop());
  }, []);

  // Timers must not outlive the hook.
  useEffect(() => stopAllBlinking, [stopAllBlinking]);

  const currentARENAAlarms = useSelector(extractARENAAlarmasFromRootState);

  useEffect(() => {
    if (preloadError) {
      setPastAdsbMissing(true);
    }
  }, [preloadError, setPastAdsbMissing]);

  useEffect(() => {
    if (adsbIsEnabled && timestampList.length > 0) {
      findPositionsByDateTimeRange(
        { dateTimes: timestampList, org: MAIN_AIRPORT, dst: MAIN_AIRPORT },
      );
    }
  }, [findPositionsByDateTimeRange, adsbIsEnabled, timestampList]);

  useEffect(() => {
    if (!realTimeMode && preloadDataSuccess && preloadedData) {
      const preloadedCurrentPositions = findNearestPlaneObservationByTimestamp(
        preloadedData,
        utcDayjs(currentPosition),
      );
      setPreloadedCurrentPositions(preloadedCurrentPositions);
      if (!preloadedCurrentPositions && !pastAdsbMissing) {
        setPastAdsbMissing(true);
      } else if (preloadedCurrentPositions && pastAdsbMissing) {
        setPastAdsbMissing(false);
      }
    }
  }, [currentPosition, preloadDataSuccess, preloadedData, pastAdsbMissing,
    realTimeMode, findPositionsByDateTimeRange, setPastAdsbMissing]);

  const getPlaneFeatureLabelOverlay = (
    planeInfo : ADSBPositions,
    position : Coordinate | undefined,
  ) => {
    if (!planeInfo.viewData?.label) return null;

    const container = document.createElement('div');
    const containerRoot = createRoot(container);
    containerRoot.render(<PlaneInfoModal planeInfo={planeInfo} />);

    const overlay = new Overlay({
      element: container,
      positioning: 'top-right',
    });
    overlay.setPosition(position);
    // Kept so removeOverlays can unmount it: one React root is created per plane per redraw.
    overlay.set(LABEL_ROOT_KEY, containerRoot);

    return overlay;
  };

  const getARENAPolygons = useCallback(() => {
    const ARENASquares = ARENALayerConfig.areasCoordinates;
    const ARENAPolygonObjects = [];

    for (let i = 0; i < ARENASquares.length; i += 1) {
      const polygonCoordinates = ARENASquares[i].coordinates;
      const transformedPolygonCoordinates = polygonCoordinates.map((coord) => transform(coord, 'EPSG:4326', availableProjections.find((projection) => projection.name === currentProjection)?.epsg as string));
      const ARENAPolygon = new Polygon([transformedPolygonCoordinates]);
      const ARENAPolygonObject = {
        polygon: ARENAPolygon,
        name: ARENASquares[i].name,
      };
      ARENAPolygonObjects.push(ARENAPolygonObject);
    }
    return ARENAPolygonObjects;
  }, [currentProjection]);

  const checkPlaneInDangerAndNotifyIfSo = useCallback((
    transformedCoordinates: number[],
    flightId: string,
  ) => {
    if (!currentARENAAlarms) {
      return false;
    }
    const alarmObj = JSON.parse(currentARENAAlarms as string);
    const ARENAPolygons = getARENAPolygons();

    for (let i = 0; i < ARENAPolygons.length; i += 1) {
      const isInside = ARENAPolygons[i].polygon.intersectsCoordinate(transformedCoordinates);
      const name = ARENAPolygons[i]?.name;
      const hasAlert = alarmObj[name] && alarmObj[name].some((v: string) => v);
      if (isInside && hasAlert) {
        if (planesInDanger.get(flightId) === undefined
          || planesInDanger.get(flightId) !== ARENAPolygons[i].name) {
          planesInDanger.set(flightId, ARENAPolygons[i].name);
          notifyADSBInDanger(
            {
              flightId: flightId || 'n/a',
              alert: transformARENAAlertsIntoLogString(alarmObj[name].reduce((acc: string, value: string) => `${acc},${value}`, '')),
              location: ARENAPolygons[i].name,
            },
          );
        }
        return true;
      }
      // if plane leaves ARENA or is no longer in danger
      if (planesInDanger.get(flightId) && planesInDanger.get(flightId) === ARENAPolygons[i].name) {
        planesInDanger.delete(flightId);
      }
    }
    return false;
  }, [currentARENAAlarms, planesInDanger, getARENAPolygons, notifyADSBInDanger]);

  const drawPlanePath = useCallback((
    map: OLMap,
    planePositionData: ADSBPositions[] | undefined,
    positionData : ADSBPositions[],
  ) => {
    if (planePositionData && planePositionData.length !== 0) {
      const planeModeS = planePositionData[0].modeS;
      const foundPosition = positionData.find((position) => position.modeS === planeModeS);
      // Never mutate planePositionData: it is the array stored in the ADS-B context.
      const trail = buildPlaneTrail(planePositionData, foundPosition);

      const points: number[][] = trail.map((data) => transform([data.longitude!, data.latitude!], 'EPSG:4326', destinationProjection));

      const lineFeature = new Feature({
        geometry: new LineString(points),
      });
      const lineStyle = new Style({
        stroke: new Stroke({
          color: 'red',
          width: 4,
        }),
      });

      lineFeature.setStyle(lineStyle);

      const lineSource : Vector<LineString> = new Vector({
        features: [lineFeature],
      });

      const lineLayer = new VectorLayer({
        source: lineSource,
      });
      lineLayer.set('name', LayerName.PlanePathLayer);
      lineLayer.setZIndex(zIndexes.ADSB - 1);

      map.addLayer(lineLayer);
    }
  }, []);

  const drawADSBData = useCallback((map: OLMap, positionData: ADSBPositions[]) => {
    // The previous features are replaced below: their blinking timers have nothing left to blink.
    stopAllBlinking();
    const iconFeatures: Feature<Point>[] = [];
    positionData.forEach((elem) => {
      const transformedCoordinates = transform([elem.longitude, elem.latitude], 'EPSG:4326', availableProjections.find((projection) => projection.name === currentProjection)?.epsg as string);
      const planeInDanger = checkPlaneInDangerAndNotifyIfSo(transformedCoordinates, elem.flight);

      const iconFeature = new Feature({
        geometry: new Point(transformedCoordinates, 'XY'),
        name: elem.icaocode,
        color: elem.viewData?.color,
        population: 4000,
        rainfall: 500,
        planeData: elem,
      });

      const defaultIconStyle = new Style({
        image: new Icon({
          color: planeInDanger ? DANGER_COLOR : elem.viewData?.color,
          rotation: elem.direction,
          height: elem.viewData?.size,
          crossOrigin: 'anonymous',
          src: PlaneIcon,
        }),
      });

      const blackIconStyle = new Style({
        image: new Icon({
          color: '#111',
          rotation: elem.direction,
          height: elem.viewData?.size,
          crossOrigin: 'anonymous',
          src: PlaneIcon,
        }),
      });

      iconFeature.setStyle(defaultIconStyle);
      iconFeatures.push(iconFeature);

      if (planeInDanger) {
        let isDefault = true;
        const intervalId = setInterval(() => {
          isDefault = !isDefault;
          iconFeature.setStyle(isDefault ? defaultIconStyle : blackIconStyle);
        }, 500);
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const stop = () => {
          clearInterval(intervalId);
          if (timeoutId) clearTimeout(timeoutId);
          blinkStoppers.current.delete(stop);
        };
        timeoutId = setTimeout(stop, 5500);
        blinkStoppers.current.add(stop);
      }

      const iconLabelPosition = iconFeature.getGeometry()?.getCoordinates();
      const iconLabel = getPlaneFeatureLabelOverlay(elem, iconLabelPosition);
      if (iconLabel) {
        map.addOverlay(iconLabel);
      }
    });

    // A plane that left the data can no longer be "in danger": forget it (the map only ever
    // grew with every flight that had been in danger).
    const reportedFlights = new Set(positionData.map((elem) => elem.flight));
    Array.from(planesInDanger.keys())
      .filter((flight) => !reportedFlights.has(flight))
      .forEach((flight) => planesInDanger.delete(flight));

    const vectorSource: Vector<Point> = new Vector({
      features: iconFeatures,
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
    });
    vectorLayer.set('name', LayerName.ASDBLayer);
    vectorLayer.setZIndex(zIndexes.ADSB);

    map.addLayer(vectorLayer);
  }, [currentProjection, checkPlaneInDangerAndNotifyIfSo, stopAllBlinking, planesInDanger]);

  const removeLayerData = useCallback((map:OLMap, layerName: LayerName) => {
    const layers = map?.getLayers();
    const ADSBLayer = layers?.getArray().find((layer) => layer.get('name') === layerName);
    if (ADSBLayer) {
      map.removeLayer(ADSBLayer);
      disposeLayerAfterRender(map, ADSBLayer as VectorLayer<Vector>);
    }
  }, []);

  const removeOverlays = useCallback((map: OLMap) => {
    const overlays = map.getOverlays().getArray();
    overlays.slice(0).forEach((overlay) => {
      map.removeOverlay(overlay);
      const labelRoot = overlay.get(LABEL_ROOT_KEY) as Root | undefined;
      // Deferred: this runs from effects, and a root cannot be unmounted synchronously while
      // React is still rendering.
      if (labelRoot) setTimeout(() => labelRoot.unmount(), 0);
    });
  }, []);

  const clearAllAdsbData = useCallback((map: OLMap) => {
    stopAllBlinking();
    removeOverlays(map);
    removeLayerData(map, LayerName.ASDBLayer);
    removeLayerData(map, LayerName.PlanePathLayer);
  }, [removeOverlays, removeLayerData, stopAllBlinking]);

  useEffect(() => {
    const handleClick = (evt: MapBrowserEvent<MouseEvent>) => {
      const { map } = evt;
      removeLayerData(map, LayerName.PlanePathLayer);

      const pixel = map?.getEventPixel(evt.originalEvent);
      let featureFound = false;

      map?.forEachFeatureAtPixel(pixel, (feature) => {
        const planeData = feature.get('planeData');
        if (planeData) {
          updateActivePlane(planeData);
          drawPlanePath(map, activePlanePositions, currentPositions);
          featureFound = true;
        }
      });

      if (!featureFound) {
        updateActivePlane(undefined);
      }
    };

    const handlePointerMove = (evt: MapBrowserEvent<MouseEvent>) => {
      const currMap = map;
      if (!evt.dragging && currMap.getTargetElement()) {
        currMap.getTargetElement().style.cursor = currMap.hasFeatureAtPixel(currMap.getEventPixel(evt.originalEvent)) ? 'pointer' : '';
      }
    };

    map.on('pointermove', handlePointerMove);
    map.on('click', handleClick);

    return () => {
      map.un('pointermove', handlePointerMove);
      map.un('click', handleClick);
    };
  }, [map, activePlanePositions, currentPositions, updateActivePlane,
    drawADSBData, drawPlanePath, removeLayerData]);

  useEffect(() => {
    if (!adsbIsEnabled) {
      clearAllAdsbData(map);
    }
  }, [map, adsbIsEnabled, clearAllAdsbData]);

  useEffect(() => {
    if (isConnected && realTimeMode) {
      clearAllAdsbData(map);
      drawPlanePath(map, activePlanePositions, currentPositions);
      drawADSBData(map, currentPositions);
    }
    // The data is cleared only if the error lasts the whole threshold: any change of the
    // dependencies (new data, reconnection) cancels the timer, which previously fired anyway
    // (its condition was a stale closure) and wiped data drawn after the connection recovered.
    let errorTimer: ReturnType<typeof setTimeout> | undefined;
    if (adsbConnectionError && realTimeMode) {
      errorTimer = setTimeout(() => clearAllAdsbData(map), ADSB_ALERT_TIMEOUT_THRESHOLD);
    }
    return () => {
      if (errorTimer) clearTimeout(errorTimer);
    };
  }, [map, isConnected, realTimeMode, activePlanePositions, currentPositions,
    adsbConnectionError, drawADSBData, drawPlanePath, clearAllAdsbData]);

  useEffect(() => {
    if (isConnected && !realTimeMode) {
      clearAllAdsbData(map);
      if (preloadDataSuccess && preloadCurrentPositions) {
        const parsedData = parsePlaneData(preloadCurrentPositions);
        drawADSBData(map, parsedData);
      }
    }
  }, [isConnected, realTimeMode, map, preloadCurrentPositions, preloadDataSuccess,
    drawADSBData, drawPlanePath, clearAllAdsbData]);

  return { drawADSBData, drawPlanePath, removeLayerData };
}

export default useADSBLayer;
